import {
  StyleSheet,
  Text,
  View,
  Dimensions,
  PixelRatio,
  TouchableOpacity,
  Platform,
  Animated,
} from "react-native";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Image } from "expo-image";
import Colours from "../../utils/Colours";
import { Ionicons } from "@expo/vector-icons";
import { useQuery } from "@tanstack/react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useNavigation } from "@react-navigation/native";
import DoingsModeBadge from "../../icons/DoingsModeBadge";

/* =========================
   Responsive Helpers
========================= */
const { width, height } = Dimensions.get("window");
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  const newSize = size * scaleFactor;
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
};

const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

// 🎨 THEME MATRIX CONFIGURATIONS
const GROUP_THEMES = {
  Whales: { label: "WHALES", primary: "#D4AC0D" },
  Ballers: { label: "BALLERS", primary: "#FF9900" },
  "Active Tables": { label: "ACTIVE TABLES", primary: Colours.PRIMARY },
  "Socially Active": { label: "SOCIALLY ACTIVE", primary: "#2ECC71" },
  "Soft life": { label: "SOFT LIFE", primary: "#5DADE2" },
};

// ⚡ REAL-TIME STATIC FEED ARRAY FOR TOP-UP PRE-RENDERS
const mockLiveTopupsFeed = [
  { id: "feed_1", name: "OBO Table 01", amount: 150000, time: "Just now" },
  { id: "feed_2", name: "Chop Life Crew", amount: 65000, time: "2m ago" },
  { id: "feed_3", name: "Frequency Boys", amount: 25000, time: "5m ago" },
];

const BrandsHigherTablesPage = ({ brandName, brandImage, brandId, displayLimit }) => {
  const axiosPrivate = useAxiosPrivate();

  const targetTiers = ["Whales", "Ballers", "Active Tables", "Soft life", "Socially Active"];

  // 1. Ticker state to force live time updates every 10 seconds (matching useIsRecentlyCreated)
  const [now, setNow] = useState(() => Date.now());

  const navigation = useNavigation();

  const handleOpenTable = (selectedItem, currentCategory) => {
    // 1. Extract raw data
    const rawTableData = selectedItem?._originalData || selectedItem;

    // 2. Extract numeric value safely
    const numericValue = Number(rawTableData?.value ?? selectedItem?.value ?? 0);

    // 3. Compute volts ONCE right here
    const computedVolts = Math.round(numericValue / 200);

    // 4. Set active mode ("activeTables" or "standard")
    const active_Tables = "activeTables";
    const currentMode = currentCategory === "Active Tables" ? "active_Tables" : "standard";

    navigation.navigate("a_Doing", {
      selectedTable: {
        ...rawTableData,
        value: numericValue,
        totalSpentDoings: rawTableData?.totalSpentDoings ?? numericValue,
        volts: computedVolts, // <-- Passed directly here!
      },
      mode: currentMode,
      fromTableProfileView: false,
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 10000); // Ticks every 10s to update time labels and clean expired items

    return () => clearInterval(interval);
  }, []);

  // 📡 INFINITE WAVE RADAR ANIMATION LOOP FOR PWA
  const waveAnimValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.loop(
      Animated.timing(waveAnimValue, {
        toValue: 1,
        duration: 1800,
        useNativeDriver: Platform.OS !== "web",
      }),
    ).start();
  }, [waveAnimValue]);

  const waveScale = waveAnimValue.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 2.5],
  });

  const waveOpacity = waveAnimValue.interpolate({
    inputRange: [0, 0.8, 1],
    outputRange: [0.6, 0.4, 0],
  });

  // 🏷️ COMPACT CURRENCY CONVERSION ENGINE
  const formatCompactCurrency = (value) => {
    if (!value || isNaN(value)) return "₦0";
    if (value >= 1000000) {
      return `${Math.floor((value / 1000000) * 10) / 10}M+`;
    }
    if (value >= 1000) {
      const roundedTens = Math.floor(value / 1000 / 10) * 10;
      const displayK = roundedTens === 0 ? Math.floor(value / 1000) : roundedTens;
      return `${displayK}K+`;
    }
    return `${value}`;
  };

  const getTickerBadgeColor = (amount) => {
    if (amount >= 100000) return Colours.PRIMARY || "#800020";
    if (amount >= 50000) return "#5DADE2";
    return "#2ECC71";
  };

  const getHigherTables = async () => {
    try {
      const response = await axiosPrivate.get(`/brandHigerTables/brandHigerTables/${brandId}`);

      if (response?.status === 200 || response?.status === 201) {
        return response.data.tables || [];
      }
      console.log("TABNLE", response.data.tables);
      return [];
    } catch (error) {
      console.error("Error fetching higher tables during refresh context:", error);
      // Throwing the error lets React Query know it failed, transitioning from 'isLoading' to 'isError'
      throw error;
    }
  };

  // ── 🔗 TANSTACK LIVE DATAFETCH QUERY INFRASTRUCTURE ──
  // const { data: tables = [], isLoading } = useQuery({
  //   queryKey: ["higherTables", brandId],
  //   queryFn: getHigherTables,
  //   enabled: !!brandId,
  //   select: (data) => {
  //     if (!Array.isArray(data)) return [];

  //     return [...data]
  //       .sort((a, b) => (b.totalSpentDoings || 0) - (a.totalSpentDoings || 0))
  //       .map((table) => {
  //         // Calculate valid live data 'doings' items sub-orders parameters
  //         const doingsOrders = (table.orders || []).filter((order) => {
  //           if (!order.items || order.items.length === 0) return false;
  //           return order.items.every((item) => item.menuType === "doings");
  //         });

  //         // Top up logic configuration calculation pipeline rules
  //         const totalDoingsOrdersCount = doingsOrders.length;
  //         const calculatedTopups = totalDoingsOrdersCount > 1 ? totalDoingsOrdersCount - 1 : 0;

  //         // Safe extract profile photos extraction array from referenced object models
  //         const processedAvatars = (table.joinedGuests || [])
  //           .map((guest) => (typeof guest === "object" ? guest?.profilePicture : null))
  //           .filter(Boolean);

  //         return {
  //           id: table._id || Math.random().toString(),
  //           name: table.tableName || "Unnamed Table",
  //           value: table.totalSpentDoings || 0,
  //           topups: calculatedTopups,
  //           friendsCount: (table.joinedGuests || []).length,
  //           users: processedAvatars,
  //           anonymous: !!table.isAnonymous,
  //           _originalData: table,
  //         };
  //       });
  //   },
  //   refetchInterval: 8000,
  //   refetchIntervalInBackground: true,
  //   staleTime: 5000,
  // });

  // ── 🔗 TANSTACK DATAFETCH + DYNAMIC TIME FILTERING ENGINE ──
  // const { data, isLoading } = useQuery({
  //   queryKey: ["higherTables", brandId],
  //   queryFn: getHigherTables,
  //   enabled: !!brandId,
  //   select: (data) => {
  //     if (!Array.isArray(data)) return { processedTables: [], liveTopupsFeed: [] };

  //     const now = new Date().getTime();
  //     const FIVE_MINUTES_MS = 5 * 60 * 1000;
  //     const FOUR_MINUTES_MS = 4 * 60 * 1000;

  //     let extractedTopupsFeed = [];

  //     const processedTables = [...data]
  //       .sort((a, b) => (b.totalSpentDoings || 0) - (a.totalSpentDoings || 0))
  //       .map((table) => {
  //         // 1. Identify valid 'doings' items sub-orders
  //         const doingsOrders = (table.orders || [])
  //           .filter((order) => {
  //             if (!order.items || order.items.length === 0) return false;
  //             return order.items.every((item) => item.menuType === "doings");
  //           })
  //           .sort((a, b) => new Date(a.orderedAt).getTime() - new Date(b.orderedAt).getTime());

  //         // 2. Map structural topups (skip first baseline order)
  //         const totalDoingsOrdersCount = doingsOrders.length;
  //         const calculatedTopups = totalDoingsOrdersCount > 1 ? totalDoingsOrdersCount - 1 : 0;

  //         // 3. Extract time-sensitive trends based on database timestamps
  //         doingsOrders.forEach((order, index) => {
  //           if (index === 0) return; // Ignore initial layout baseline setup order

  //           const orderTime = new Date(order.orderedAt).getTime();
  //           const timePassed = now - orderTime;

  //           // 🌟 CRITICAL CONDITION MATRIX: Filter new topups in the last 5min, keep visible on trend for 4min
  //           if (timePassed >= 0 && timePassed <= FIVE_MINUTES_MS) {
  //             if (timePassed <= FOUR_MINUTES_MS) {
  //               const minutesAgo = Math.floor(timePassed / 60000);
  //               extractedTopupsFeed.push({
  //                 id: order.orderToken || order._id || Math.random().toString(),
  //                 name: table.tableName || "VIP Table",
  //                 amount: order.orderTotalAmount || 0,
  //                 timeLabel: minutesAgo === 0 ? "Just now" : `${minutesAgo}m ago`,
  //                 rawTime: orderTime,
  //               });
  //             }
  //           }
  //         });

  //         const processedAvatars = (table.joinedGuests || [])
  //           .map((guest) => (typeof guest === "object" ? guest?.profilePicture : null))
  //           .filter(Boolean);

  //         return {
  //           id: table._id || Math.random().toString(),
  //           name: table.tableName || "Unnamed Table",
  //           value: table.totalSpentDoings || 0,
  //           topups: calculatedTopups,
  //           friendsCount: (table.joinedGuests || []).length,
  //           users: processedAvatars,
  //           anonymous: !!table.isAnonymous,
  //           _originalData: table,
  //         };
  //       });

  //     // Sort live feed items so the absolute newest appear at the very top
  //     extractedTopupsFeed.sort((a, b) => b.rawTime - a.rawTime);

  //     return { processedTables, liveTopupsFeed: extractedTopupsFeed };
  //   },
  //   refetchInterval: 8000, // Frequent background processing ensures timely removal after 4 mins
  //   refetchIntervalInBackground: true,
  //   staleTime: 5000,
  // });

  // 2. Fetch raw tables without computing time-sensitive state inside select
  const { data: rawTables = [], isLoading } = useQuery({
    queryKey: ["higherTables", brandId],
    queryFn: getHigherTables,
    enabled: !!brandId,
    refetchInterval: 8000,
    refetchIntervalInBackground: true,
    staleTime: 5000,
  });

  const processAndCategorizeTables = (dataset) => {
    const categories = {
      Whales: [],
      Titans: [],
      Ballers: [],
      "Active Tables": [],
      "Socially Active": [],
      "Soft life": [],
    };

    dataset.forEach((table) => {
      const spend = table.value || 0;

      // if (spend >= 900000) {
      //   categories["Whales"].push(table);
      // } else if (spend >= 200000) {
      //   categories["Ballers"].push(table);
      // } else {
      //   // Range processing framework: 199k and lower rules
      //   if (table.topups >= 1) {
      //     categories["Active Tables"].push(table);
      //   } else if (spend <= 49000 && table.friendsCount >= 2) {
      //     categories["Socially Active"].push(table);
      //   } else {
      //     categories["Soft life"].push(table);
      //   }
      // }

      if (spend >= 300000) {
        categories["Whales"].push(table);
      } else if (spend >= 200000) {
        categories["Titans"].push(table);
      } else if (spend >= 80000) {
        categories["Ballers"].push(table);
      } else {
        // Range processing framework: 199k and lower rules
        if (table.topups >= 1) {
          categories["Active Tables"].push(table);
        } else if (spend <= 49000 && table.friendsCount >= 2) {
          categories["Socially Active"].push(table);
        } else {
          categories["Soft life"].push(table);
        }
      }
    });

    // Sub-tier presentation sort mapping pipelines
    categories["Whales"].sort((a, b) => b.value - a.value);
    categories["Titans"].sort((a, b) => b.value - a.value);
    categories["Ballers"].sort((a, b) => b.value - a.value);
    categories["Active Tables"].sort((a, b) => b.value - a.value);
    categories["Socially Active"].sort((a, b) => b.friendsCount - a.friendsCount);
    categories["Soft life"].sort((a, b) => b.value - a.value);

    return categories;
  };

  // const { processedTables, liveTopupsFeed } = useMemo(() => {
  //   if (!Array.isArray(rawTables)) {
  //     return { processedTables: [], liveTopupsFeed: [] };
  //   }

  //   const FIVE_MINUTES_MS = 5 * 60 * 1000;
  //   const FOUR_MINUTES_MS = 4 * 60 * 1000;

  //   let extractedTopupsFeed = [];

  //   const processed = [...rawTables]
  //     // FIX 1: Filter out tables with 0 "doings" spend so regular orders don't leak into tiers
  //     .filter((table) => (table.totalSpentDoings || 0) > 0)
  //     .sort((a, b) => (b.totalSpentDoings || 0) - (a.totalSpentDoings || 0))
  //     .map((table) => {
  //       // Identify valid 'doings' sub-orders
  //       const doingsOrders = (table.orders || [])
  //         .filter((order) => {
  //           // Check order-level menuType as fallback
  //           if (order.menuType === "doings") return true;
  //           if (!order.items || order.items.length === 0) return false;
  //           // FIX 2: Use .some() instead of .every() so top-ups aren't dropped on mixed items
  //           return order.items.some((item) => item.menuType === "doings");
  //         })
  //         .sort((a, b) => {
  //           const timeA = new Date(a.orderedAt || a.createdAt || 0).getTime();
  //           const timeB = new Date(b.orderedAt || b.createdAt || 0).getTime();
  //           return timeA - timeB;
  //         });

  //       const totalDoingsOrdersCount = doingsOrders.length;
  //       const calculatedTopups = totalDoingsOrdersCount > 1 ? totalDoingsOrdersCount - 1 : 0;

  //       // Extract time-sensitive feed items calculated against local 'now'
  //       doingsOrders.forEach((order, index) => {
  //         if (index === 0) return; // Ignore baseline setup order

  //         const orderTimestamp = order.orderedAt || order.createdAt;
  //         if (!orderTimestamp) return;

  //         const orderTime = new Date(orderTimestamp).getTime();
  //         const timePassed = now - orderTime;

  //         if (timePassed >= 0 && timePassed <= FIVE_MINUTES_MS) {
  //           if (timePassed <= FOUR_MINUTES_MS) {
  //             const minutesAgo = Math.floor(timePassed / 60000);
  //             extractedTopupsFeed.push({
  //               id: order.orderToken || order._id || Math.random().toString(),
  //               name: table.tableName || "VIP Table",
  //               amount: order.orderTotalAmount || 0,
  //               timeLabel: minutesAgo === 0 ? "Just now" : `${minutesAgo}m ago`,
  //               rawTime: orderTime,
  //             });
  //           }
  //         }
  //       });

  //       const processedAvatars = (table.joinedGuests || [])
  //         .map((guest) => (typeof guest === "object" ? guest?.profilePicture : null))
  //         .filter(Boolean);

  //       return {
  //         id: table._id || Math.random().toString(),
  //         name: table.tableName || "Unnamed Table",
  //         value: table.totalSpentDoings || 0,
  //         topups: calculatedTopups,
  //         friendsCount: (table.joinedGuests || []).length,
  //         users: processedAvatars,
  //         anonymous: !!table.isAnonymous,
  //         _originalData: table,
  //       };
  //     });

  //   // Sort live feed items newest first
  //   extractedTopupsFeed.sort((a, b) => b.rawTime - a.rawTime);

  //   return { processedTables: processed, liveTopupsFeed: extractedTopupsFeed };
  // }, [rawTables, now]);

  // const processedTables = data?.processedTables || [];
  // const liveTopupsFeed = data?.liveTopupsFeed || [];

  // ⚙️ RIGID PIPELINE SEGREGATION ROUTER

  const { doingsCategories, regularCategories, liveTopupsFeed } = useMemo(() => {
    if (!Array.isArray(rawTables)) {
      return { doingsCategories: {}, regularCategories: {}, liveTopupsFeed: [] };
    }

    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const FOUR_MINUTES_MS = 4 * 60 * 1000;
    let extractedTopupsFeed = [];

    const doingsList = [];
    const regularList = [];

    rawTables.forEach((table) => {
      const orders = table.orders || [];

      const isAllOrdersPaid =
        orders.length > 0 &&
        orders.every(
          (order) =>
            order.isPaid === true || order.paymentStatus === "paid" || order.status === "paid",
        );

      // Identify valid 'doings' sub-orders for table categorization
      const doingsOrders = orders.filter((order) => {
        if (order.menuType === "doings") return true;
        if (!order.items || order.items.length === 0) return false;
        return order.items.some((item) => item.menuType === "doings");
      });

      const hasDoings = doingsOrders.length > 0 || (table.totalSpentDoings || 0) > 0;

      // Sort ALL orders chronologically for universal top-up tracking (regardless of menuType)
      const sortedOrders = [...orders].sort((a, b) => {
        const timeA = new Date(a.orderedAt || a.createdAt || 0).getTime();
        const timeB = new Date(b.orderedAt || b.createdAt || 0).getTime();
        return timeA - timeB;
      });

      const totalOrdersCount = sortedOrders.length;
      const calculatedTopups = totalOrdersCount > 1 ? totalOrdersCount - 1 : 0;

      // Extract live feed items from ALL orders (skipping index 0, which is the initial table creation order)
      sortedOrders.forEach((order, index) => {
        if (index === 0) return;
        const orderTimestamp = order.orderedAt || order.createdAt;
        if (!orderTimestamp) return;

        const orderTime = new Date(orderTimestamp).getTime();
        const timePassed = now - orderTime;

        if (timePassed >= 0 && timePassed <= FIVE_MINUTES_MS) {
          if (timePassed <= FOUR_MINUTES_MS) {
            const minutesAgo = Math.floor(timePassed / 60000);
            extractedTopupsFeed.push({
              id: order.orderToken || order._id || Math.random().toString(),
              name: table.tableName || "VIP Table",
              amount: order.orderTotalAmount || 0,
              timeLabel: minutesAgo === 0 ? "Just now" : `${minutesAgo}m ago`,
              rawTime: orderTime,
            });
          }
        }
      });

      const processedAvatars = (table.joinedGuests || [])
        .map((guest) => (typeof guest === "object" ? guest?.profilePicture : null))
        .filter(Boolean);

      const formattedTable = {
        id: table._id || Math.random().toString(),
        name: table.tableName || "Unnamed Table",
        value:
          (hasDoings ? table.totalSpentDoings : table.totalSpent) ||
          table.estimatedTotalBill ||
          table.value ||
          0,
        topups: calculatedTopups,
        friendsCount: (table.joinedGuests || []).length,
        users: processedAvatars,
        anonymous: !!table.isAnonymous,
        hasDoingsMode: hasDoings,
        isAllOrdersPaid: isAllOrdersPaid,
        _originalData: table,
      };

      if (hasDoings) {
        doingsList.push(formattedTable);
      } else {
        regularList.push(formattedTable);
      }
    });

    extractedTopupsFeed.sort((a, b) => b.rawTime - a.rawTime);

    return {
      doingsCategories: processAndCategorizeTables(doingsList),
      regularCategories: processAndCategorizeTables(regularList),
      liveTopupsFeed: extractedTopupsFeed,
    };
  }, [rawTables, now]);

  // const { doingsCategories, regularCategories, liveTopupsFeed } = useMemo(() => {
  //   if (!Array.isArray(rawTables)) {
  //     return { doingsCategories: {}, regularCategories: {}, liveTopupsFeed: [] };
  //   }

  //   const FIVE_MINUTES_MS = 5 * 60 * 1000;
  //   const FOUR_MINUTES_MS = 4 * 60 * 1000;
  //   let extractedTopupsFeed = [];

  //   const doingsList = [];
  //   const regularList = [];

  //   rawTables.forEach((table) => {
  //     const orders = table.orders || [];

  //     // Check if table has orders and ALL of them are paid.
  //     // Adjust the condition (e.g. order.isPaid === true or order.paymentStatus === "paid" or order.status === "paid")
  //     // to match your backend model.
  //     const isAllOrdersPaid =
  //       orders.length > 0 &&
  //       orders.every(
  //         (order) =>
  //           order.isPaid === true || order.paymentStatus === "paid" || order.status === "paid",
  //       );

  //     // Identify valid 'doings' sub-orders
  //     const doingsOrders = orders.filter((order) => {
  //       if (order.menuType === "doings") return true;
  //       if (!order.items || order.items.length === 0) return false;
  //       return order.items.some((item) => item.menuType === "doings");
  //     });

  //     const hasDoings = doingsOrders.length > 0 || (table.totalSpentDoings || 0) > 0;

  //     // Sort doings orders for feed tracking
  //     doingsOrders.sort((a, b) => {
  //       const timeA = new Date(a.orderedAt || a.createdAt || 0).getTime();
  //       const timeB = new Date(b.orderedAt || b.createdAt || 0).getTime();
  //       return timeA - timeB;
  //     });

  //     const totalDoingsOrdersCount = doingsOrders.length;
  //     const calculatedTopups = totalDoingsOrdersCount > 1 ? totalDoingsOrdersCount - 1 : 0;

  //     // Extract live feed items
  //     doingsOrders.forEach((order, index) => {
  //       if (index === 0) return;
  //       const orderTimestamp = order.orderedAt || order.createdAt;
  //       if (!orderTimestamp) return;

  //       const orderTime = new Date(orderTimestamp).getTime();
  //       const timePassed = now - orderTime;

  //       if (timePassed >= 0 && timePassed <= FIVE_MINUTES_MS) {
  //         if (timePassed <= FOUR_MINUTES_MS) {
  //           const minutesAgo = Math.floor(timePassed / 60000);
  //           extractedTopupsFeed.push({
  //             id: order.orderToken || order._id || Math.random().toString(),
  //             name: table.tableName || "VIP Table",
  //             amount: order.orderTotalAmount || 0,
  //             timeLabel: minutesAgo === 0 ? "Just now" : `${minutesAgo}m ago`,
  //             rawTime: orderTime,
  //           });
  //         }
  //       }
  //     });

  //     const processedAvatars = (table.joinedGuests || [])
  //       .map((guest) => (typeof guest === "object" ? guest?.profilePicture : null))
  //       .filter(Boolean);

  //     const formattedTable = {
  //       id: table._id || Math.random().toString(),
  //       name: table.tableName || "Unnamed Table",
  //       value:
  //         (hasDoings ? table.totalSpentDoings : table.totalSpent) ||
  //         table.estimatedTotalBill ||
  //         table.value ||
  //         0,
  //       topups: calculatedTopups,
  //       friendsCount: (table.joinedGuests || []).length,
  //       users: processedAvatars,
  //       anonymous: !!table.isAnonymous,
  //       hasDoingsMode: hasDoings,
  //       isAllOrdersPaid: isAllOrdersPaid, // <--- ADDED FLAG HERE
  //       _originalData: table,
  //     };

  //     if (hasDoings) {
  //       doingsList.push(formattedTable);
  //     } else {
  //       regularList.push(formattedTable);
  //     }
  //   });

  //   extractedTopupsFeed.sort((a, b) => b.rawTime - a.rawTime);

  //   return {
  //     doingsCategories: processAndCategorizeTables(doingsList),
  //     regularCategories: processAndCategorizeTables(regularList),
  //     liveTopupsFeed: extractedTopupsFeed,
  //   };
  // }, [rawTables, now]);

  //const processedDataCategories = processAndCategorizeTables(processedTables); //processAndCategorizeTables(tables)

  // const renderItem = ({ item, index, themeColor, category }) => {
  //   // Structural filter parameters for conditional visibility states (Soft Life rule)
  //   const shouldHideSoftLifeFinancials = category === "Soft life" && item.value < 45000;

  //   return (
  //     <View
  //       key={item.id || index}
  //       style={[
  //         styles.tableCardContainer,
  //         {
  //           shadowColor: themeColor,
  //           borderColor: Platform.OS === "android" ? "transparent" : "#F2F2F7",
  //           borderWidth: Platform.OS === "android" ? 0 : 1,
  //         },
  //       ]}
  //     >
  //       <TouchableOpacity style={styles.rowCardContent} onPress={() => handleOpenTable(item)}>
  //         <Text style={styles.rank}>{index + 1}</Text>

  //         <View style={styles.infoContainer}>
  //           <Text style={styles.tableName}>{item.name}</Text>

  //           <View style={styles.metricRowGroup}>
  //             {category === "Active Tables" ? (
  //               <View style={styles.metricRowGroupWrapped}>
  //                 <View style={styles.activeCapsuleLabel}>
  //                   <Ionicons
  //                     name="trending-up"
  //                     size={dynamicSize(10)}
  //                     color={Colours.PRIMARY || "#333"}
  //                     style={{ marginRight: 4 }}
  //                   />
  //                   <Text style={styles.activeLabelTxt}>Top ups</Text>
  //                   <Text style={[styles.activeValueTxt, { color: themeColor }]}>
  //                     {" "}
  //                     {item.topups}
  //                   </Text>
  //                 </View>

  //                 <View style={styles.miniSeparatorDot} />

  //                 <View
  //                   style={[
  //                     styles.activeCapsuleTag,
  //                     { backgroundColor: `${themeColor}10`, borderColor: `${themeColor}40` },
  //                   ]}
  //                 >
  //                   <Ionicons
  //                     name="flash"
  //                     size={dynamicSize(11)}
  //                     color="#FF9900"
  //                     style={{ marginRight: dynamicSpacing(3) }}
  //                   />
  //                   <Text style={styles.activeLabelTxt}>Voltage</Text>
  //                   <Text style={styles.voltageValueTxt}>
  //                     {" "}
  //                     {Math.round(item.value / 200)} Volts
  //                   </Text>
  //                 </View>
  //               </View>
  //             ) : category === "Socially Active" ? (
  //               <>
  //                 <View style={styles.labelCapsuleTag}>
  //                   <Ionicons
  //                     name="people-outline"
  //                     size={dynamicSize(12)}
  //                     color={Colours.PRIMARY}
  //                     style={{ marginRight: 4 }}
  //                   />
  //                   <Text style={styles.valueLabelTxt}>{`${item.friendsCount} Active Guests`}</Text>
  //                 </View>
  //                 <View style={styles.miniSeparatorDot} />
  //                 <View
  //                   style={[
  //                     styles.amountCapsuleTag,
  //                     { backgroundColor: `${themeColor}15`, borderColor: `${themeColor}40` },
  //                   ]}
  //                 >
  //                   <Text
  //                     style={[
  //                       styles.slantedVividAmount,
  //                       { color: themeColor, fontStyle: "normal" },
  //                     ]}
  //                   >
  //                     <Ionicons name="people" size={dynamicSize(11)} color={themeColor} />
  //                     {` ${item.friendsCount} Tagged`}
  //                   </Text>
  //                 </View>
  //               </>
  //             ) : (
  //               <>
  //                 <View style={styles.labelCapsuleTag}>
  //                   <Ionicons
  //                     name="trending-up"
  //                     size={dynamicSize(12)}
  //                     color={Colours.PRIMARY || "#333"}
  //                     style={{ marginRight: 4 }}
  //                   />
  //                   <Text style={styles.valueLabelTxt}>Table Value</Text>
  //                 </View>
  //                 {!shouldHideSoftLifeFinancials && (
  //                   <>
  //                     <View style={styles.miniSeparatorDot} />
  //                     <View
  //                       style={[
  //                         styles.amountCapsuleTag,
  //                         { backgroundColor: `${themeColor}15`, borderColor: `${themeColor}40` },
  //                       ]}
  //                     >
  //                       <Text style={[styles.slantedVividAmount, { color: themeColor }]}>
  //                         {formatCompactCurrency(item.value)}
  //                       </Text>
  //                     </View>
  //                   </>
  //                 )}
  //               </>
  //             )}
  //           </View>
  //         </View>

  //         {item.anonymous ? (
  //           <Text style={styles.anonymous}>A/N</Text>
  //         ) : (
  //           <View style={styles.avatarStack}>
  //             {item.users &&
  //               item.users
  //                 .slice(0, 4)
  //                 .map((avatar, i) => (
  //                   <Image
  //                     key={i}
  //                     source={{ uri: avatar }}
  //                     style={[styles.avatar, { marginLeft: i === 0 ? 0 : -dynamicSpacing(12) }]}
  //                   />
  //                 ))}
  //           </View>
  //         )}
  //       </TouchableOpacity>
  //     </View>
  //   );
  // };

  const renderItem = ({ item, index, themeColor, category }) => {
    const shouldHideSoftLifeFinancials = category === "Soft life" && item.value < 45000;

    return (
      <View key={item.id || index} style={[styles.tableCardContainer, { shadowColor: themeColor }]}>
        <TouchableOpacity
          style={styles.rowCardContent}
          onPress={() => handleOpenTable(item, category)}
        >
          {/* Rank number is now anchored back perfectly centered to the full card height */}
          <Text style={styles.rank}>{index + 1}</Text>

          {/* Column wrapper isolates text stack behavior from the rank alignment axis */}
          <View style={styles.rightContentColumn}>
            {/* Header row: Title and Avatar split cleanly with space between */}
            <View style={styles.tableCardHeaderMainRow}>
              <View style={styles.tableNameWrapper}>
                <Text style={styles.tableName}>{item.name}</Text>
                {item.hasDoingsMode && <DoingsModeBadge size={dynamicSize(15)} color="#D4AF37" />}
                {item.isAllOrdersPaid && <DoingsModeBadge size={dynamicSize(15)} color="#2ECC71" />}
              </View>

              {item.anonymous ? (
                /* Render a deterministic DiceBear avatar seeded by the item's unique ID */
                <Image
                  source={{
                    uri: `https://api.dicebear.com/7.x/pixel-art/svg?seed=${
                      item._id || item.id || item.name || "anonymous"
                    }`,
                  }}
                  style={styles.avatar}
                />
              ) : (
                <View style={styles.avatarStackInline}>
                  {item.users?.slice(0, 4).map((avatar, i) => (
                    <Image
                      key={i}
                      source={{ uri: avatar }}
                      style={[styles.avatar, { marginLeft: i === 0 ? 0 : -dynamicSpacing(12) }]}
                    />
                  ))}
                </View>
              )}
            </View>

            {/* Metrics Row falls naturally underneath */}
            <View style={styles.metricRowGroup}>
              {category === "Active Tables" ? (
                <View style={styles.metricRowGroupWrapped}>
                  <View style={styles.activeCapsuleLabel}>
                    <Ionicons
                      name="trending-up"
                      size={dynamicSize(10)}
                      color={Colours.PRIMARY || "#333"}
                      style={{ marginRight: 4 }}
                    />
                    <Text style={styles.activeLabelTxt}>Top ups</Text>
                    <Text style={[styles.activeValueTxt, { color: themeColor }]}>
                      {" "}
                      {item.topups}
                    </Text>
                  </View>
                  <View style={styles.miniSeparatorDot} />
                  <View
                    style={[
                      styles.activeCapsuleTag,
                      { backgroundColor: `${themeColor}10`, borderColor: `${themeColor}40` },
                    ]}
                  >
                    <Ionicons
                      name="flash"
                      size={dynamicSize(11)}
                      color="#FF9900"
                      style={{ marginRight: dynamicSpacing(3) }}
                    />
                    <Text style={styles.activeLabelTxt}>Voltage</Text>
                    <Text style={styles.voltageValueTxt}>
                      {" "}
                      {Math.round(item.value / 200)} Volts
                    </Text>
                  </View>
                </View>
              ) : category === "Socially Active" ? (
                <>
                  <View style={styles.labelCapsuleTag}>
                    <Ionicons
                      name="people-outline"
                      size={dynamicSize(12)}
                      color={Colours.PRIMARY}
                      style={{ marginRight: 4 }}
                    />
                    <Text style={styles.valueLabelTxt}>{`${item.friendsCount} Active Guests`}</Text>
                  </View>
                  <View style={styles.miniSeparatorDot} />
                  <View
                    style={[
                      styles.amountCapsuleTag,
                      { backgroundColor: `${themeColor}15`, borderColor: `${themeColor}40` },
                    ]}
                  >
                    <Text style={[styles.slantedVividAmount, { color: themeColor }]}>
                      <Ionicons name="people" size={dynamicSize(11)} color={themeColor} />
                      {` ${item.friendsCount} Tagged`}
                    </Text>
                  </View>
                </>
              ) : (
                <>
                  {/* If value is 50k or below, render ONLY the Voltage tag */}
                  {item.value <= 100000 ? (
                    <View
                      style={[
                        styles.activeCapsuleTag,
                        { backgroundColor: `${themeColor}10`, borderColor: `${themeColor}40` },
                      ]}
                    >
                      <Ionicons
                        name="flash"
                        size={dynamicSize(11)}
                        color="#FF9900"
                        style={{ marginRight: dynamicSpacing(3) }}
                      />
                      <Text style={styles.activeLabelTxt}>Voltage</Text>
                      <Text style={styles.voltageValueTxt}>
                        {" "}
                        {Math.round(item.value / 200)} Volts
                      </Text>
                    </View>
                  ) : (
                    /* For values over 50k, show "Table Value · [Amount]" */
                    !shouldHideSoftLifeFinancials && (
                      <>
                        <View style={styles.labelCapsuleTag}>
                          <Ionicons
                            name="trending-up"
                            size={dynamicSize(12)}
                            color={Colours.PRIMARY || "#333"}
                            style={{ marginRight: 4 }}
                          />
                          <Text style={styles.valueLabelTxt}>Table Value</Text>
                        </View>

                        <View style={styles.miniSeparatorDot} />

                        <View
                          style={[
                            styles.amountCapsuleTag,
                            { backgroundColor: `${themeColor}15`, borderColor: `${themeColor}40` },
                          ]}
                        >
                          <Text style={[styles.slantedVividAmount, { color: themeColor }]}>
                            {formatCompactCurrency(item.value)}
                          </Text>
                        </View>
                      </>
                    )
                  )}
                </>
              )}
            </View>
          </View>
        </TouchableOpacity>
      </View>
    );
  };

  // const targetTiers = ["Whales", "Ballers", "Active Tables", "Soft life", "Socially Active"];
  // 1. Check if there is at least one table across your target tiers that has items
  const hasDoingsTables = targetTiers.some(
    (tierName) => doingsCategories[tierName] && doingsCategories[tierName].length > 0,
  );
  return (
    <View style={styles.listWrapperBlock}>
      {/* BRAND HEADER BLOCK */}
      <View style={styles.headerBlock}>
        <View style={styles.textContainer}>
          <Text style={styles.headerTitle}>Doings Trend</Text>
          <Text style={styles.headerSubtitle}>
            Discover Ballers, Active and Highest spending tables @{brandName}
          </Text>
        </View>
        {brandName && brandImage && (
          <Image source={{ uri: brandImage }} style={styles.brandCircleImage} />
        )}
      </View>

      {/* ── ⚡ LIVE TABLE TOP-UPS SECTION (VERTICAL STACK) ── */}
      {liveTopupsFeed.length > 0 && (
        <View style={styles.tickerOuterWrapper}>
          <View style={styles.tickerHeaderRow}>
            <View style={styles.liveIndicatorContainer}>
              <View style={styles.waveNodeLayout}>
                <Animated.View
                  style={[
                    styles.liveWaveRing,
                    { transform: [{ scale: waveScale }], opacity: waveOpacity },
                  ]}
                />
                <View style={styles.liveWaveCenter} />
              </View>
              <Text style={styles.liveIndicatorText}>LIVE TABLE TOP-UPS</Text>
            </View>
            <Text style={styles.tickerSubLink}>Activity Feed</Text>
          </View>

          <View style={styles.verticalFeedStackContainer}>
            {liveTopupsFeed.map((feedItem, index) => {
              const dynamicColorTheme = getTickerBadgeColor(feedItem.amount);
              const showAmountBadge = (feedItem.amount || 0) > 19000;

              return (
                <View key={feedItem.id} style={styles.liveVerticalItemRow}>
                  <View style={styles.liveVerticalItemLeft}>
                    {index === 0 ? (
                      <View style={styles.waveNodeLayout2}>
                        <Animated.View
                          style={[
                            styles.toppingWaveRing,
                            { transform: [{ scale: waveScale }], opacity: waveOpacity },
                          ]}
                        />
                        <View style={styles.toppingWaveCenter} />
                      </View>
                    ) : (
                      <View style={styles.balloonSpaceReplacerDot} />
                    )}

                    <View style={styles.metaTextBlock}>
                      <Text style={styles.liveCardTableName}>{feedItem.name}</Text>
                      <Text style={styles.liveCardToppingLabel}>Top up</Text>
                    </View>
                  </View>

                  <View style={styles.liveVerticalItemRight}>
                    {/* 🟢 ONLY SHOW AMOUNT BADGE IF GREATER THAN 19K */}
                    {showAmountBadge && (
                      <View
                        style={[
                          styles.tickerFullSolidCapsule,
                          { backgroundColor: dynamicColorTheme },
                        ]}
                      >
                        <Text style={styles.tickerFullCapsuleText}>
                          {formatCompactCurrency(feedItem.amount)}
                        </Text>
                      </View>
                    )}
                    <Text style={styles.liveCardTimeText}>{feedItem.timeLabel}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      )}

      {/* ── SECTION 1: SPENT WITH DOINGS MODE ── */}
      {hasDoingsTables && (
        <>
          <View style={styles.doingsSectionContainer}>
            {/* Full-width header banner with centered title (no pulse dot) */}
            <View style={styles.doingsHeaderBanner}>
              <Text style={styles.doingsHeaderTitle}>Spent on Doings Mode</Text>
              <DoingsModeBadge size={dynamicSize(15)} color="#D4AF37" />
            </View>

            {targetTiers.map((tierName) => {
              const groupItems = doingsCategories[tierName]?.slice(0, displayLimit);
              if (!groupItems || groupItems.length === 0) return null;
              const theme = GROUP_THEMES[tierName];

              return (
                <View key={`doings-${tierName}`} style={styles.groupContainerBlock}>
                  <View style={styles.liveIndicatorContainer}>
                    <View style={[styles.liveDotPulse, { backgroundColor: theme.primary }]} />
                    <Text style={styles.liveIndicatorText}>{theme.label}</Text>
                  </View>
                  <View style={styles.cardsStackWrapper}>
                    {groupItems.map((tableItem, index) =>
                      renderItem({
                        item: tableItem,
                        index: index,
                        themeColor: theme.primary,
                        category: tierName,
                      }),
                    )}
                  </View>
                </View>
              );
            })}
          </View>

          <View style={styles.sectionDividerLine} />
        </>
      )}

      {/* ── SECTION 2: REGULAR TABLES ── */}
      <View style={styles.regularSectionContainer}>
        {targetTiers.map((tierName) => {
          const groupItems = regularCategories[tierName]?.slice(0, displayLimit);
          if (!groupItems || groupItems.length === 0) return null;
          const theme = GROUP_THEMES[tierName];

          return (
            <View key={`regular-${tierName}`} style={styles.groupContainerBlock}>
              <View style={styles.liveIndicatorContainer}>
                <View style={[styles.liveDotPulse, { backgroundColor: theme.primary }]} />
                <Text style={styles.liveIndicatorText}>{theme.label}</Text>
              </View>
              <View style={styles.cardsStackWrapper}>
                {groupItems.map((tableItem, index) =>
                  renderItem({
                    item: tableItem,
                    index: index,
                    themeColor: theme.primary,
                    category: tierName,
                  }),
                )}
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

export default BrandsHigherTablesPage;

const styles = StyleSheet.create({
  listWrapperBlock: {
    paddingTop: dynamicSpacing(10),
    paddingHorizontal: dynamicSpacing(4),
  },

  headerBlock: {
    paddingHorizontal: dynamicSpacing(4),
    flexDirection: "row", // Aligns text container and image side-by-side
    justifyContent: "space-between", // Pushes text to the far left, image to the far right
    alignItems: "center", // Vertically centers the text with the circle image
    marginVertical: dynamicSpacing(10),
  },

  textContainer: {
    flex: 1, // Allows the text container to take up remaining space safely
    paddingRight: dynamicSpacing(10), // Prevents long text names from bumping directly into the image
  },

  headerTitle: {
    fontSize: dynamicFontSize(18),
    fontFamily: "Sen-Bold",
    color: Colours.PRIMARY,
  },

  headerSubtitle: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Regular",
    color: Colours.DARKLIGHTGRAY,
    marginTop: dynamicSpacing(5),
  },

  brandCircleImage: {
    width: dynamicSize(46), // Adjust height/width values to match your design scale
    height: dynamicSize(46),
    borderRadius: dynamicSize(23), // Half of width/height guarantees a perfect circle shape
    backgroundColor: Colours.LIGHTESTGRAY, // Soft fallback background while image loads
    resizeMode: "cover",
  },

  groupContainerBlock: {
    marginBottom: dynamicSpacing(20),
  },
  cardsStackWrapper: {
    marginTop: dynamicSpacing(10),
  },
  liveIndicatorContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(6),
    paddingHorizontal: dynamicSpacing(4),
    marginVertical: dynamicSpacing(4),
  },
  liveDotPulse: {
    width: dynamicSize(8),
    height: dynamicSize(8),
    borderRadius: 50,
  },
  liveIndicatorText: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Bold",
    color: "#666",
    letterSpacing: 0.5,
  },
  //   waveNodeLayout: {
  //   width: dynamicSize(16),
  //   height: dynamicSize(16),
  //   justifyContent: "center",
  //   alignItems: "center",
  //   marginRight: dynamicSpacing(2),
  // },
  waveNodeLayout: {
    width: dynamicSize(16),
    height: dynamicSize(16),
    justifyContent: "center",
    alignItems: "center",
    marginRight: dynamicSpacing(2),
  },
  waveNodeLayout2: {
    width: dynamicSize(6),
    height: dynamicSize(6),
    justifyContent: "center",
    alignItems: "center",
    marginHorizontal: dynamicSpacing(13),
  },
  toppingWaveCenter: {
    width: dynamicSize(8),
    height: dynamicSize(8),
    borderRadius: 4,
    backgroundColor: Colours.PRIMARY,
    position: "absolute",
    zIndex: 2,
  },
  toppingWaveRing: {
    width: dynamicSize(10),
    height: dynamicSize(10),
    borderRadius: 50,
    borderWidth: 1.5,
    borderColor: Colours.PRIMARY,
    position: "absolute",
    zIndex: 1,
  },

  tableCardContainer: {
    backgroundColor: "white",
    borderRadius: dynamicSize(14),
    marginBottom: dynamicSpacing(14),
    paddingHorizontal: dynamicSpacing(14),
    paddingVertical: dynamicSpacing(10),
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.12,
    shadowRadius: 10,
    elevation: 4,
  },
  rowCardContent: {
    flexDirection: "row",
    // alignItems: "center",
    // alignItems: "flex-start",
    alignItems: "center",
  },
  rank: {
    fontSize: dynamicFontSize(18),
    fontFamily: "Sen-Bold",
    color: "#C8C8C8",
    width: dynamicSpacing(28),
    textAlign: "left",
    alignSelf: "center",
  },
  infoContainer: {
    flex: 1,
    // paddingRight: dynamicSpacing(8),
  },
  tableName: {
    fontSize: dynamicFontSize(15),
    fontFamily: "Sen-Bold",
    color: "#1F1F1F",
    paddingRight: dynamicSpacing(2),
  },
  metricRowGroup: {
    flexDirection: "row",
    alignItems: "center",
    //marginTop: dynamicSpacing(6),
  },
  labelCapsuleTag: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F2F2F7",
    paddingHorizontal: dynamicSpacing(8),
    paddingVertical: dynamicSpacing(4),
    borderRadius: dynamicSize(6),
  },
  valueLabelTxt: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Regular",
    color: "#666",
  },
  miniSeparatorDot: {
    width: dynamicSize(4),
    height: dynamicSize(4),
    borderRadius: dynamicSize(2),
    backgroundColor: "#D1D1D6",
    marginHorizontal: dynamicSpacing(8),
    alignSelf: "center",
  },
  amountCapsuleTag: {
    paddingHorizontal: dynamicSpacing(10),
    paddingVertical: dynamicSpacing(4),
    borderRadius: dynamicSize(6),
    borderWidth: 0.8,
  },
  slantedVividAmount: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Bold",
    letterSpacing: 0.3,
  },
  avatarStack: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: dynamicSize(70),
    paddingLeft: dynamicSpacing(4),
  },
  avatar: {
    width: dynamicSize(30),
    height: dynamicSize(30),
    borderRadius: dynamicSize(15),
    borderWidth: 1.5,
    borderColor: "white",
  },
  anonymous: {
    fontSize: dynamicFontSize(12),
    color: "#9E9E9E",
    fontFamily: "Sen-Regular",
  },
  activeCapsuleTag: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F2F2F7",
    paddingHorizontal: dynamicSpacing(8),
    paddingVertical: dynamicSpacing(4),
    borderRadius: dynamicSize(6),
    borderWidth: 0.8,
  },
  activeCapsuleLabel: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F2F2F7",
    paddingHorizontal: dynamicSpacing(8),
    paddingVertical: dynamicSpacing(4),
    borderRadius: dynamicSize(6),
  },
  activeLabelTxt: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Regular",
    color: "#666667",
  },
  activeValueTxt: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Bold",
  },
  voltageValueTxt: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Bold",
    color: Colours.PRIMARY,
  },
  metricRowGroupWrapped: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    // marginTop: dynamicSpacing(6),
    gap: dynamicSpacing(2),
  },

  // ── 📡 RADATING LIVE WAVE EMITTER INDICATION DESIGNERS ──
  // waveNodeLayout: {
  //   width: dynamicSize(16),
  //   height: dynamicSize(16),
  //   justifyContent: "center",
  //   alignItems: "center",
  //   marginRight: dynamicSpacing(2),
  // },
  liveWaveCenter: {
    width: dynamicSize(8),
    height: dynamicSize(8),
    borderRadius: 4,
    backgroundColor: "#2ECC71",
    position: "absolute",
    zIndex: 2,
  },
  liveWaveRing: {
    width: dynamicSize(10),
    height: dynamicSize(10),
    borderRadius: 50,
    borderWidth: 1.5,
    borderColor: "#2ECC71",
    position: "absolute",
    zIndex: 1,
  },

  // ── ⚡ VERTICAL TICKER RENDER WORKSPACE STYLING ──
  tickerOuterWrapper: {
    marginBottom: dynamicSpacing(22),
    marginTop: dynamicSpacing(4),
  },
  tickerHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: dynamicSpacing(4),
    marginBottom: dynamicSpacing(10),
  },
  tickerSubLink: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Regular",
    color: Colours.PRIMARY,
  },
  verticalFeedStackContainer: {
    gap: dynamicSpacing(12),
    paddingHorizontal: dynamicSpacing(4),
  },
  liveVerticalItemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "white",
    paddingVertical: dynamicSpacing(12),
    paddingHorizontal: dynamicSpacing(14),
    borderRadius: dynamicSize(14),

    // shadowColor: "#000",
    // shadowOffset: { width: 0, height: 4 },
    // shadowOpacity: 0.05,
    // shadowRadius: 6,
    // elevation: 2,
  },
  liveVerticalItemLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(12),
  },
  vividCelebrationBalloon: {
    width: dynamicSize(32),
    height: dynamicSize(32),
  },
  balloonSpaceReplacerDot: {
    width: dynamicSize(6),
    height: dynamicSize(6),
    borderRadius: 50,
    backgroundColor: "#E5E5EA",
    marginHorizontal: dynamicSpacing(13),
  },
  metaTextBlock: {
    justifyContent: "center",
  },
  liveCardTableName: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#1F1F1F",
  },
  liveCardToppingLabel: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Regular",
    color: "#8E8E93",
    marginTop: dynamicSpacing(1),
  },
  liveVerticalItemRight: {
    alignItems: "flex-end",
    gap: dynamicSpacing(4),
  },
  tickerFullSolidCapsule: {
    paddingHorizontal: dynamicSpacing(12),
    paddingVertical: dynamicSpacing(5),
    borderRadius: dynamicSize(6),
    justifyContent: "center",
    alignItems: "center",
  },
  tickerFullCapsuleText: {
    fontSize: dynamicFontSize(12),
    fontFamily: "Sen-Bold",
    color: "white",
  },
  liveCardTimeText: {
    fontSize: dynamicFontSize(10),
    fontFamily: "Sen-Regular",
    color: "#C7C7CC",
  },

  rightContentColumn: {
    flex: 1, // Takes up the remaining horizontal space safely
    flexDirection: "column",
    justifyContent: "center",
  },
  tableCardHeaderMainRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  avatarStackInline: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },

  doingsSectionContainer: {
    // backgroundColor: "#F7F6F2", // Clean, soft distinct warm grey/off-white background
    // Negative margin cancels out listWrapperBlock padding to touch screen edges horizontally
    marginHorizontal: -dynamicSpacing(4),
    paddingTop: dynamicSpacing(16),
    paddingBottom: dynamicSpacing(12),
    marginBottom: dynamicSpacing(20),
  },

  doingsHeaderBanner: {
    flexDirection: "row",

    width: "100%",
    alignItems: "center", // Perfectly centers the text horizontally
    justifyContent: "center",
    paddingVertical: dynamicSpacing(5),
    paddingHorizontal: dynamicSpacing(16),
    marginBottom: dynamicSpacing(12),
  },

  doingsHeaderTitle: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY, // Clean dark grey color
    textAlign: "center",
    letterSpacing: 0.8,
    paddingRight: dynamicSpacing(2),

    textTransform: "uppercase", // Gives it a clean section banner feel
  },

  sectionDividerLine: {
    height: 1,
    backgroundColor: "#E5E5EA",
    marginVertical: dynamicSpacing(10),
    marginHorizontal: dynamicSpacing(4),
  },

  regularSectionContainer: {
    backgroundColor: "transparent",
    paddingHorizontal: dynamicSpacing(4),
    paddingBottom: dynamicSpacing(30),
  },
  sectionHeaderBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(6),
    paddingHorizontal: dynamicSpacing(4),
    marginBottom: dynamicSpacing(12),
  },
  sectionHeaderTitle: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: Colours.PRIMARY,
    letterSpacing: 0.5,
  },

  tableNameWrapper: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    paddingRight: dynamicSpacing(8),
  },
  goldBadge: {
    marginLeft: dynamicSpacing(4),
  },
});
